import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Typography, useMediaQuery } from "@mui/material";

/* styled */
import {
    StyledTreeViewContainer,
    StyledTreeViewWrap,
    StyledTreeViewContent,
    StyledTreeViewFoldButton,
    StyledTreeItemRoot,
    StyledTreeView,
} from "./styled";
import clsx from "clsx";
import {
    IconChevronLeft,
    IconChevronRight,
    IconHierarchyFolded,
    IconHierarchyOpened,
    IconMoreHoriz,
    IconRefresh,
} from "@mzc-cloudplex/icons";
import { useTreeItem } from "@mui/lab";
import { IconButton, Tooltip, TextField } from "@mzc-pdc/ui";
import { Menu } from "@components_v2/menu";
import { useTheme } from "@mui/styles";
import EllipsisTooltip from "@components_v2/ellipsis-tooltip";
import theme from "@styles/theme";
import i18n from "@lib/i18n";

const CustomContent = React.forwardRef(function CustomContent(props, ref) {
    const {
        classes,
        className,
        label,
        nodeId,
        icon: iconProp,
        type,
        expansionIcon,
        displayIcon,
        depth,
        menuList,
        isOverCurrent,
        foldDisabled,
        onClick,
        onClickRefresh,
        onClickExpand,
        onChangeField,
        onSubmitField,
        onDragOver,
        onDragLeave,
        onDrop,
        draggable,
        onDragStart,
        onDragEnd,
        dropPosition,
        isDragSource,
    } = props;

    const { disabled, expanded, selected } = useTreeItem(nodeId);
    const [clicked, setClicked] = useState(false);
    const [folderName, setFolderName] = useState("");

    const icon = iconProp;

    const handleExpansionClick = (event) => {
        event.stopPropagation();
        // handleExpansion(event);
        if (onClickExpand) onClickExpand();
    };

    const handleSelectionClick = (event) => {
        event.stopPropagation();
        //if (!expanded) handleExpansion(event);
        //if (!selected) handleSelection(event);

        if (onClick) onClick(event);
    };

    return (
        <div
            className={clsx(className, classes.root, {
                [classes.expanded]: expanded,
                [classes.selected]: selected,
                clicked,
                [classes.disabled]: disabled,
                overCurrent: isOverCurrent,
                dragSource: isDragSource,
                dropBefore: dropPosition === "before",
                dropAfter: dropPosition === "after" || dropPosition === "intoFirst",
            })}
            style={
                dropPosition && dropPosition !== "into"
                    ? // 삽입 위치 인디케이터 들여쓰기 — 형제 삽입은 현재 depth, 첫 자식 삽입(intoFirst)은 자식 depth 기준
                      { "--drop-indicator-left": `${16 + (depth + (dropPosition === "intoFirst" ? 1 : 0)) * 16}px` }
                    : undefined
            }
            onMouseDown={(e) => {
                e.stopPropagation();
            }}
            onClick={handleSelectionClick}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            // 드래그 가능 행은 mousedown 시 포커스를 행 자신이 받게 한다 — li로 포커스가 가면
            // MUI TreeItem이 트리 루트 ul로 동기 refocus를 수행해 Chrome의 드래그 개시가 취소됨
            tabIndex={draggable ? -1 : undefined}
            ref={ref}
        >
            {Array.from(Array(depth), (v, index) => (
                <span key={`spacing-${index}`} className={"spacing"} />
            ))}
            {!foldDisabled && (
                <span onClick={handleExpansionClick} className={clsx(classes.iconContainer, "expandToggle")}>
                    {expanded ? (
                        <IconHierarchyOpened size={12} htmlColor={"#6f6f83"} />
                    ) : (
                        <IconHierarchyFolded size={12} htmlColor={"#6f6f83"} />
                    )}
                </span>
            )}
            {icon && <span className={classes.iconContainer}>{icon}</span>}
            {nodeId !== "create" ? (
                <EllipsisTooltip
                    text={label}
                    placement="top"
                    maxWidth={"100%"}
                    typographyProps={{ color: theme.palette.greySecondary.main }}
                    boxProps={{ className: classes.label }}
                    variant={"body1_400"}
                />
            ) : (
                <TextField
                    autoFocus
                    size={"small"}
                    value={folderName}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        e.stopPropagation();
                        setFolderName(e.target.value);
                    }}
                    onKeyUp={(e) => {
                        e.stopPropagation();
                        if ((e.code === "Enter" || e.code === "NumpadEnter") && folderName && onSubmitField) {
                            onSubmitField(folderName);
                        }
                    }}
                    onBlur={(e) => {
                        e.stopPropagation();
                        if (onSubmitField) onSubmitField(folderName);
                    }}
                />
            )}
            {menuList && (
                <Grid
                    item
                    onClick={(e) => {
                        e.stopPropagation();
                        const target = e.target;
                        if (target.classList.contains("MuiSvgIcon-root")) setClicked(true);
                        else setClicked(false);
                    }}
                >
                    <Menu
                        menuWidth={160}
                        placement={"right"}
                        type={"icon"}
                        label={<IconMoreHoriz size={16} />}
                        color={"inherit"}
                        size={"small"}
                        listSize={"small"}
                        list={menuList}
                        preventPropagation={false}
                    />
                </Grid>
            )}
            {onClickRefresh && (
                <Tooltip title={i18n.t(`common::label::Refresh`, `Refresh`)}>
                    <IconButton
                        size={"small"}
                        circled
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onClickRefresh) onClickRefresh();
                        }}
                    >
                        <IconRefresh size={16} color={"greySecondary"} />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    );
});

export const TreeItem = forwardRef((props, ref) => {
    // draggable 등 드래그 소스 속성은 행(Content)에만 적용 — li 루트에 스프레드되면 하위 트리 전체가 드래그 대상이 됨
    // 드롭 핸들러도 마찬가지로 분리한다 — li에 걸리면 자식 행·스켈레톤 영역까지 부모 행의 드롭존으로 잡혀
    // 드롭 위치 판정(행 높이 기준)이 어긋나고, 자식으로 이동할 때 dragleave의 contains 가드에 막혀 강조가 남는다
    const { draggable, onDragStart, onDragEnd, dropPosition, onDragOver, onDragLeave, onDrop, isDragSource, ...rest } = props;
    return (
        <StyledTreeItemRoot
            {...rest}
            ContentComponent={CustomContent}
            ContentProps={{
                type: props.type,
                depth: props.depth,
                menuList: props.menuList,
                foldDisabled: props.foldDisabled,
                isOverCurrent: props.isOverCurrent,
                onClick: props.onClick,
                onClickRefresh: props.onClickRefresh,
                onClickExpand: props.onClickExpand,
                onSubmitField: props.onSubmitField,
                onDragOver,
                onDragLeave,
                onDrop,
                draggable,
                onDragStart,
                onDragEnd,
                dropPosition,
                isDragSource,
            }}
            ref={ref}
        />
    );
});
TreeItem.displayName = "TreeItem";

export const TreeView = (props) => {
    // const renderTree = (nodes) => (
    //   <TreeItem key={nodes.id} nodeId={nodes.id} label={nodes.name}>
    //     {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
    //   </TreeItem>
    // );

    return (
        <StyledTreeView
            defaultExpanded={props.expanded}
            defaultCollapseIcon={<IconHierarchyFolded size={12} />}
            defaultExpandIcon={<IconHierarchyOpened size={12} />}
            {...props}
        >
            {/*{props.data?.map((treeItem) => (*/}
            {/*  <TreeItem key={treeItem.id} nodeId={treeItem.id} label={treeItem.name}>*/}
            {/*    {Array.isArray(treeItem.children) ? treeItem.children.map((node) => renderTree(node)) : null}*/}
            {/*  </TreeItem>*/}
            {/*))}*/}
        </StyledTreeView>
    );
};

export const TreeViewWrap = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isFold, setIsFold] = useState(false);
    const isDown1024 = useMediaQuery(theme.breakpoints.down("lg"));
    const isUp1024 = useMediaQuery(theme.breakpoints.up("lg"));

    useEffect(() => {
        if (isDown1024) {
            return setIsFold(true);
        }
    }, [isDown1024]);

    useEffect(() => {
        if (isUp1024) {
            return setIsFold(false);
        }
    }, [isUp1024]);

    return (
        <StyledTreeViewContainer>
            <StyledTreeViewWrap fold={props.fold} isFold={isFold}>
                <StyledTreeViewContent>{props.children}</StyledTreeViewContent>
                {props.fold && (
                    <StyledTreeViewFoldButton isFold={isFold}>
                        <Tooltip
                            title={isFold ? t("common::label::Expand") : t("common::label::Fold")}
                            placement={"right"}
                        >
                            <button onClick={() => setIsFold(!isFold)}>
                                {isFold ? <IconChevronRight size={12} /> : <IconChevronLeft size={12} />}
                            </button>
                        </Tooltip>
                    </StyledTreeViewFoldButton>
                )}
            </StyledTreeViewWrap>
        </StyledTreeViewContainer>
    );
};
